import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { INSIGHTS, INSIGHT_CATEGORIES } from "@/insights";
import NewsletterForm from "@/components/NewsletterForm";

export default function InsightsPage() {
  const [cat, setCat] = useState("All");
  const filtered = cat === "All" ? INSIGHTS : INSIGHTS.filter((i) => i.category === cat);

  return (
    <div data-testid="insights-page" className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-8">
          <span className="eyebrow">Insights</span>
          <h1 className="mt-4 text-4xl lg:text-6xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.02]">
            Field notes from <span className="text-slate-500">the plant floor.</span>
          </h1>
          <p className="mt-6 text-[17px] text-slate-600 leading-[1.7] max-w-2xl">
            Short essays on cash, constraints, and consequential trade-offs. Written for operators, not for other consultants.
          </p>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-10">
        {INSIGHT_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`text-[13px] font-medium px-4 py-2 rounded-full border transition-colors ${
              cat === c
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((i, idx) => (
          <Link
            key={i.slug}
            to={`/insights/${i.slug}`}
            data-testid={`insight-card-${i.slug}`}
            className="ind-card p-7 flex flex-col justify-between group hover:-translate-y-0.5 transition-transform"
          >
            <div>
              <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-slate-500">
                <span>{String(idx + 1).padStart(2, "0")}</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{i.category}</span>
              </div>
              <h3 className="mt-4 text-xl font-semibold tracking-tight text-slate-900 group-hover:text-[#712ae2] transition-colors leading-snug">
                {i.title}
              </h3>
              <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{i.excerpt}</p>
            </div>
            <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
              <div className="text-xs text-slate-500">{i.date} · {i.read}</div>
              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#712ae2] group-hover:translate-x-0.5 transition-all" />
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-slate-500">No insights in this category yet.</div>
      )}

      <section className="mt-16">
        <NewsletterForm source="insights-index" />
      </section>

      <section className="mt-16 rounded-2xl bg-[#0F172A] text-white p-10 lg:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <span className="text-[11px] tracking-[0.22em] uppercase text-slate-400">From words to numbers</span>
          <h2 className="mt-3 text-2xl lg:text-3xl font-semibold tracking-tight">
            Ready to run these ideas against your P&L?
          </h2>
        </div>
        <Link to="/diagnostic" data-testid="insights-cta-diagnostic" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 text-sm font-semibold px-6 py-3">
          Run the Diagnostic <ArrowUpRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
}
